import type { FC, ReactNode } from 'react'
import type { SampleKind } from '@/constants/materials'

import { useEffect, useRef } from 'react'
import { createRng } from '@/lib/cloud/rng'

interface MaterialSampleProps {
    kind: SampleKind
    label: string
    className?: string
}

type Dot = [number, number, number]

const SEEDS: Record<SampleKind, number> = { timber: 11, concrete: 23, steel: 37, glass: 41, brick: 53 }

/**
 * Deterministic point patterns in a unit square, one per material.
 */
const pattern = (kind: SampleKind): Array<Dot> => {
    const rng = createRng(SEEDS[kind])
    const dots: Array<Dot> = []

    if (kind === 'timber') {
        for (let line = 0; line < 22; line++) {
            const y0 = 0.08 + line * 0.04
            const wave = 0.006 + rng() * 0.01

            for (let k = 0; k < 90; k++) {
                const x = 0.06 + (k / 90) * 0.66
                const y = y0 + Math.sin(x * 9 + line * 0.7) * wave + (rng() - 0.5) * 0.004

                dots.push([x, y, 0.45 + 0.4 * rng()])
            }
        }

        for (let ring = 1; ring < 9; ring++) {
            for (let k = 0; k < ring * 14; k++) {
                const a = (k / (ring * 14)) * Math.PI * 2
                const r = ring * 0.024

                dots.push([0.86 + Math.cos(a) * r * 0.7, 0.5 + Math.sin(a) * r * 1.6, 0.75])
            }
        }

        return dots
    }

    if (kind === 'concrete') {
        for (let k = 0; k < 1800; k++) dots.push([0.05 + rng() * 0.9, 0.05 + rng() * 0.9, 0.25 + 0.4 * rng()])

        for (let stone = 0; stone < 34; stone++) {
            const cx = 0.1 + rng() * 0.8
            const cy = 0.1 + rng() * 0.8
            const r = 0.015 + rng() * 0.035

            for (let k = 0; k < 40; k++) {
                const a = rng() * Math.PI * 2
                const d = r * Math.sqrt(rng())

                dots.push([cx + Math.cos(a) * d, cy + Math.sin(a) * d, 0.85])
            }
        }

        return dots
    }

    if (kind === 'steel') {
        const step = 0.16

        for (let i = 0; i <= 5; i++) {
            for (let j = 0; j <= 5; j++) {
                const x = 0.1 + i * step
                const y = 0.1 + j * step

                for (let k = 0; k < 10; k++) dots.push([x + (rng() - 0.5) * 0.012, y + (rng() - 0.5) * 0.012, 1])

                if (i < 5) for (let k = 0; k < 24; k++) dots.push([x + (k / 24) * step, y, 0.7])
                if (j < 5) for (let k = 0; k < 24; k++) dots.push([x, y + (k / 24) * step, 0.7])
                if (i < 5 && j < 5 && (i + j) % 2 === 0) for (let k = 0; k < 30; k++) dots.push([x + (k / 30) * step, y + (k / 30) * step, 0.45])
            }
        }

        return dots
    }

    if (kind === 'glass') {
        for (let plane = 0; plane < 3; plane++) {
            const offset = plane * 0.07

            for (let k = 0; k < 520; k++) {
                const u = rng()
                const v = rng()

                dots.push([0.12 + offset + u * 0.52, 0.12 + offset + v * 0.62 - u * 0.12, plane === 1 ? 0.25 : 0.55])
            }
        }

        for (let k = 0; k < 140; k++) {
            const v = k / 140

            dots.push([0.12, 0.12 + v * 0.62, 0.95], [0.26, 0.26 + v * 0.62, 0.95])
        }

        return dots
    }

    const rows = 10
    const rowHeight = 0.08

    for (let row = 0; row < rows; row++) {
        const y0 = 0.1 + row * rowHeight
        const shift = row % 2 === 0 ? 0 : 0.085

        for (let brick = -1; brick < 6; brick++) {
            const x0 = 0.06 + shift + brick * 0.17

            for (let k = 0; k < 70; k++) {
                const x = x0 + rng() * 0.155
                const y = y0 + rng() * (rowHeight - 0.014)

                if (x < 0.05 || x > 0.95) continue

                dots.push([x, y, 0.35 + 0.5 * rng()])
            }
        }
    }

    return dots
}

/**
 * A small scanned sample of a material, drawn in cream points on a canvas.
 */
export const MaterialSample: FC<MaterialSampleProps> = ({ kind, label, className }): ReactNode => {
    const canvasRef = useRef<HTMLCanvasElement>(null)

    useEffect(() => {
        const canvas = canvasRef.current
        const context = canvas?.getContext('2d')

        if (!canvas || !context) return

        const dots = pattern(kind)

        const draw = (): void => {
            const rect = canvas.getBoundingClientRect()
            const ratio = Math.min(window.devicePixelRatio || 1, 2)
            const size = Math.max(1, Math.round(Math.min(rect.width, rect.height)))
            const width = Math.round(rect.width)
            const height = Math.round(rect.height)

            canvas.width = width * ratio
            canvas.height = height * ratio
            context.setTransform(ratio, 0, 0, ratio, 0, 0)
            context.clearRect(0, 0, width, height)

            const ox = (width - size) / 2
            const oy = (height - size) / 2
            const dot = Math.max(1, size / 260)

            for (const [x, y, a] of dots) {
                context.fillStyle = `rgba(216, 210, 196, ${a.toFixed(2)})`
                context.fillRect(ox + x * size, oy + y * size, dot, dot)
            }
        }

        draw()

        const observer = new ResizeObserver(draw)

        observer.observe(canvas)

        return () => observer.disconnect()
    }, [kind])

    return <canvas ref={canvasRef} role='img' aria-label={label} className={className} />
}
